import { CarePlanInterventionResponse } from '~/lib/types/carePlan/carePlans';
import {
  ChakraFlex,
  FAIconName,
  TertiaryTagDropdown,
  useToastMessage,
} from '@equip.health/ui';
import { InterventionStatusDropdownOptions } from '~/lib/constants/carePlan.constants';
import { useUpdateInterventionMutation } from '~/store/services/carePlan';
import { CarePlanInterventionStatus } from '~/lib/types/carePlan/CarePlanInterventionStatus';
import { useEffect, useState } from 'react';
import { CarePlanInterventionRowAvatarSection } from '~/components/maud/patients/profile/tabs/careplan/CarePlanInterventionRowAvatarSection';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { CarePlanEvents } from '~/analytics/carePlanEvents';
import { InterventionText } from './InterventionText';
import { useGetInterventionCategoriesEnumQuery } from '~/store/services/refCarePlanEnums';

type Props = {
  treatmentEpisodeId: string;
  intervention: CarePlanInterventionResponse;
  isForPhaseOrStageClassification?: boolean;
};

export const CarePlanInterventionRow = ({
  treatmentEpisodeId,
  intervention,
  isForPhaseOrStageClassification,
}: Props) => {
  const { trackEvent } = useAnalytics();

  const [updateIntervention, { isError: isUpdateInterventionError }] =
    useUpdateInterventionMutation();

  const { createToast } = useToastMessage();

  const [isHovered, setIsHovered] = useState(false);

  const { data: categories } = useGetInterventionCategoriesEnumQuery();

  useEffect(() => {
    if (isUpdateInterventionError) {
      createToast({
        id: 'intervention-status-update-error',
        icon: FAIconName.exclamationTriangleRegular,
        text: "An error occurred updating the intervention's status. Please try again later.",
        variant: 'critical',
      });
    }
  }, [isUpdateInterventionError]);

  return (
    <ChakraFlex
      alignItems="center"
      data-testid={`care-plan-intervention-row-${intervention.id}`}
      gap="12px"
      justifyContent="space-between"
      marginBottom="12px"
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
    >
      <ChakraFlex alignItems="center" gap="8px" minWidth={0}>
        <InterventionText categories={categories} intervention={intervention} />
      </ChakraFlex>
      <ChakraFlex alignItems="center" gap="12px" whiteSpace="nowrap">
        <TertiaryTagDropdown
          menuPlacement="bottom"
          onDropdownOpen={() => {
            trackEvent(
              isForPhaseOrStageClassification
                ? CarePlanEvents.clickOnInterventionStatusMenuInTreatmentPhaseStageComponent
                : CarePlanEvents.clickOnInterventionStatusMenuInGoalCard,
            );
          }}
          onOptionClick={(newStatus) => {
            trackEvent(
              isForPhaseOrStageClassification
                ? CarePlanEvents.clickOnInterventionStatusMenuOptionInTreatmentPhaseStageComponent
                : CarePlanEvents.clickOnInterventionStatusMenuOptionInGoalCard,
            );
            updateIntervention({
              treatmentEpisodeId,
              interventionId: intervention.id,
              body: {
                statusCode: newStatus as CarePlanInterventionStatus,
              },
            });
          }}
          options={InterventionStatusDropdownOptions}
          value={intervention.status.code}
        />
        <CarePlanInterventionRowAvatarSection
          intervention={intervention}
          isForPhaseOrStageClassification
          isRowHovered={isHovered}
          treatmentEpisodeId={treatmentEpisodeId}
        />
      </ChakraFlex>
    </ChakraFlex>
  );
};
